import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import TourCard from "@/components/TourCard";
import { api } from "@/lib/api";
import { SlidersHorizontal, Search } from "lucide-react";

const CATEGORIES = ["All", "Group Tours", "Customized Tours"];
const REGIONS = ["All", "Europe", "Asia", "North America", "South America", "Antarctica", "Africa", "Oceania", "India"];
const TYPE_OPTIONS = ["Beach", "Mountain", "Desert", "Jungle", "Island", "Heritage"];
const MAX_BUDGET = 1200000;

export default function Tours() {
  const [params, setParams] = useSearchParams();
  const [category, setCategory] = useState(params.get("category") || "All");
  const [region, setRegion] = useState(params.get("region") || "All");
  const [maxPrice, setMaxPrice] = useState(MAX_BUDGET);
  const [duration, setDuration] = useState("All");
  const [search, setSearch] = useState("");
  const [tours, setTours] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (category !== "All") q.category = category;
    if (region !== "All") q.region = region;
    if (maxPrice < MAX_BUDGET) q.maxPrice = maxPrice;
    if (duration === "1-5") { q.minDuration = 1; q.maxDuration = 5; }
    else if (duration === "6-9") { q.minDuration = 6; q.maxDuration = 9; }
    else if (duration === "10+") { q.minDuration = 10; }
    if (search) q.search = search;
    api.get("/tours", { params: q }).then((r) => setTours(r.data)).finally(() => setLoading(false));
    // update URL
    const p = new URLSearchParams();
    if (category !== "All") p.set("category", category);
    if (region !== "All") p.set("region", region);
    setParams(p, { replace: true });
  }, [category, region, maxPrice, duration, search]);

  useEffect(() => {
    setCategory(params.get("category") || "All");
    setRegion(params.get("region") || "All");
  }, [params]);

  const toggleType = (label) =>
    setSelectedTypes((prev) => prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label]);

  const displayedTours = useMemo(() => (
    selectedTypes.length === 0
      ? tours
      : tours.filter((t) => selectedTypes.some((x) => (t.types || []).includes(x)))
  ), [tours, selectedTypes]);

  return (
    <div className="bg-ivory min-h-[80vh]">
      <div className="bg-maroon text-amber-50 py-14 relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none opacity-30" />
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="text-amber-300 text-sm font-semibold tracking-widest uppercase mb-3">International & India Tours from Kolkata</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">{category === "Group Tours" ? "Best International Group Tours from Kolkata" : category === "Customized Tours" ? "Affordable Customized International Tours from Kolkata" : "International & India Tour Packages from Kolkata"}</h1>
          <p className="mt-4 text-amber-50/90 text-lg max-w-2xl">Budget-friendly, masterfully planned vacations escorted from Kolkata by a trusted Bengali travel agency. Every tour is senior-friendly and explores its region in depth.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 -mt-8 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl border border-amber-900/10 p-6" data-testid="filters-bar">
          <div className="flex items-center gap-2 text-maroon mb-4">
            <SlidersHorizontal size={18} /> <span className="font-display font-bold text-lg">Refine Your Search</span>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Search tours…"
                className="w-full h-12 pl-10 pr-4 rounded-lg border-2 border-amber-900/15 focus:border-amber-600 focus:outline-none bg-ivory text-base"
                data-testid="filter-search" />
            </div>
            <select value={category} onChange={(e)=>setCategory(e.target.value)}
              className="h-12 px-4 rounded-lg border-2 border-amber-900/15 focus:border-amber-600 focus:outline-none bg-ivory text-base font-medium"
              data-testid="filter-category">
              {CATEGORIES.map((c)=><option key={c}>{c}</option>)}
            </select>
            <select value={region} onChange={(e)=>setRegion(e.target.value)}
              className="h-12 px-4 rounded-lg border-2 border-amber-900/15 focus:border-amber-600 focus:outline-none bg-ivory text-base font-medium"
              data-testid="filter-region">
              {REGIONS.map((c)=><option key={c}>{c}</option>)}
            </select>
            <select value={duration} onChange={(e)=>setDuration(e.target.value)}
              className="h-12 px-4 rounded-lg border-2 border-amber-900/15 focus:border-amber-600 focus:outline-none bg-ivory text-base font-medium"
              data-testid="filter-duration">
              <option value="All">Any duration</option>
              <option value="1-5">1–5 days</option>
              <option value="6-9">6–9 days</option>
              <option value="10+">10+ days</option>
            </select>
          </div>
          <div className="mt-4">
            <label className="text-sm font-semibold text-slate-700" data-testid="filter-price-label">
              Budget: {maxPrice >= MAX_BUDGET ? "₹12 Lakh+ (any budget)" : `up to ₹${(maxPrice/100000).toFixed(1).replace(/\.0$/, "")} Lakh`}
            </label>
            <input type="range" min={50000} max={MAX_BUDGET} step={25000} value={maxPrice}
              onChange={(e)=>setMaxPrice(Number(e.target.value))}
              className="w-full accent-amber-600 mt-2" data-testid="filter-price" />
          </div>
          <div className="mt-5 pt-4 border-t border-amber-900/10">
            <div className="text-sm font-semibold text-slate-700 mb-2">Type of trip</div>
            <div className="flex flex-wrap gap-2" data-testid="filter-types">
              {TYPE_OPTIONS.map((label) => (
                <button key={label} type="button" onClick={()=>toggleType(label)}
                  className={`px-4 h-9 rounded-full text-sm font-semibold border-2 transition-colors ${selectedTypes.includes(label) ? "bg-maroon text-amber-50 border-maroon" : "bg-ivory text-slate-700 border-amber-900/15 hover:border-amber-600"}`}
                  data-testid={`filter-type-${label.toLowerCase()}`}>{label}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {loading ? (
          <div className="text-center text-slate-500 py-20">Loading tours…</div>
        ) : displayedTours.length === 0 ? (
          <div className="text-center text-slate-500 py-20" data-testid="tours-empty">
            <div className="font-display text-2xl text-maroon mb-2">No tours match your filters</div>
            <div>Try adjusting your budget, region or trip type.</div>
          </div>
        ) : (
          <>
            <div className="text-slate-600 mb-6 font-medium" data-testid="tours-count">{displayedTours.length} tour{displayedTours.length!==1?"s":""} found</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayedTours.map((t)=><TourCard key={t.id} tour={t} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
